import { useEffect } from 'react';

const FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
].join(', ');

// The selector already drops disabled and tabindex="-1" elements; on top of
// that we skip anything explicitly hidden from assistive technology.
const getFocusable = (node) =>
    Array.from(node.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
        (el) => !el.closest('[hidden]') && el.getAttribute('aria-hidden') !== 'true'
    );

/**
 * Keeps keyboard focus inside a container while it is active.
 *
 * On activation focus moves into the container (unless a child already took it,
 * e.g. through autoFocus) and it is restored to the previously focused element
 * when the container goes away.
 *
 * @param {React.RefObject<HTMLElement>} containerRef - The trapped container
 * @param {Object} options
 * @param {boolean} options.active - Whether the trap is armed (default: true)
 * @param {React.RefObject<HTMLElement>} options.initialFocusRef - Element to focus first
 */
function useFocusTrap(containerRef, { active = true, initialFocusRef } = {}) {
    useEffect(() => {
        const node = containerRef.current;
        if (!active || !node) return undefined;

        const previouslyFocused = document.activeElement;

        if (initialFocusRef?.current) {
            initialFocusRef.current.focus();
        } else if (!node.contains(document.activeElement)) {
            const [first] = getFocusable(node);
            (first || node).focus();
        }

        const handleKeyDown = (event) => {
            if (event.key !== 'Tab') return;

            const focusable = getFocusable(node);
            if (focusable.length === 0) {
                event.preventDefault();
                node.focus();
                return;
            }

            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            const current = document.activeElement;

            if (event.shiftKey && (current === first || !node.contains(current))) {
                event.preventDefault();
                last.focus();
                return;
            }
            if (!event.shiftKey && current === last) {
                event.preventDefault();
                first.focus();
            }
        };

        node.addEventListener('keydown', handleKeyDown);

        return () => {
            node.removeEventListener('keydown', handleKeyDown);
            if (previouslyFocused && document.contains(previouslyFocused)) {
                previouslyFocused.focus();
            }
        };
    }, [containerRef, active, initialFocusRef]);
}

export default useFocusTrap;
